import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import posthog from 'posthog-js';
import { store } from './store';
import { getCurrentUser } from './store/slices/authSlice';
import { useAppSelector } from './store/hooks';
import { CurrencyProvider } from './contexts/CurrencyContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import PageShell from './components/layout/PageShell';
import PageLoadingSkeleton from './components/common/PageLoadingSkeleton';
import { usePageTracking } from './hooks/usePageTracking';
import { usePushNotifications } from './hooks/usePushNotifications';
import { trackSessionRestored } from './lib/analytics';
import AdvisorChat from './components/advisor/AdvisorChat';

// Route-level code splitting via React.lazy
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DeFiRiskLandingPage = lazy(() => import('./pages/DeFiRiskLandingPage'));
const KoinlyAlternativePage = lazy(() => import('./pages/KoinlyAlternativePage'));
const CoinTrackerAlternativePage = lazy(() => import('./pages/CoinTrackerAlternativePage'));
const ZapperAlternativePage = lazy(() => import('./pages/ZapperAlternativePage'));
const DeBankAlternativePage = lazy(() => import('./pages/DeBankAlternativePage'));
const RotkiAlternativePage = lazy(() => import('./pages/RotkiAlternativePage'));
const CoinStatsAlternativePage = lazy(() => import('./pages/CoinStatsAlternativePage'));
const MultiChainPortfolioPage = lazy(() => import('./pages/MultiChainPortfolioPage'));
const DeFiLpTaxCalculatorPage = lazy(() => import('./pages/DeFiLpTaxCalculatorPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const WalletsPage = lazy(() => import('./pages/WalletsPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const PortfolioImportPage = lazy(() => import('./pages/PortfolioImportPage'));
const RiskPage = lazy(() => import('./pages/RiskPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const RiskIntelligenceCenterPage = lazy(() => import('./pages/RiskIntelligenceCenterPage'));
const DeFiHealthPage = lazy(() => import('./pages/DeFiHealthPage'));
const ImpermanentLossPage = lazy(() => import('./pages/ImpermanentLossPage'));
const RebalancingPlannerPage = lazy(() => import('./pages/RebalancingPlannerPage'));
const RebalancingAdvisorPage = lazy(() => import('./pages/RebalancingAdvisorPage'));
const WalletHubPage = lazy(() => import('./pages/WalletHubPage'));
const AlertRulesPage = lazy(() =>
  import('./pages/AlertRulesPage').then((m) => ({ default: m.AlertRulesPage }))
);
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const AuditDashboardPage = lazy(() => import('./pages/admin/AuditDashboardPage'));
const PrivacySettingsPage = lazy(() => import('./pages/PrivacySettingsPage'));
const DataExportPage = lazy(() => import('./pages/DataExportPage'));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const SecurityDefensesPage = lazy(() => import('./pages/SecurityDefensesPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const DebugPage = lazy(() => import('./pages/DebugPage'));
const ExportStatusPage = lazy(() => import('./pages/ExportStatusPage'));
const AlertDeliveryHistoryPage = lazy(() => import('./pages/AlertDeliveryHistoryPage'));
const InvoicesPage = lazy(() => import('./pages/billing/InvoicesPage'));
const BillingSuccessPage = lazy(() => import('./pages/billing/BillingSuccessPage'));
const SubscriptionPage = lazy(() => import('./pages/billing/SubscriptionPage'));
const PlansPage = lazy(() => import('./pages/billing/PlansPage'));
const UpgradePage = lazy(() => import('./pages/billing/UpgradePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const CampaignHistoryPage = lazy(() => import('./pages/settings/CampaignHistoryPage'));
const SecuritySettingsPage = lazy(() => import('./pages/settings/SecuritySettingsPage'));
const DeveloperPortalPage = lazy(() => import('./pages/DeveloperPortalPage'));
const DeveloperDocsPage = lazy(() => import('./pages/DeveloperDocsPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PortfolioAnalyticsPage = lazy(() => import('./pages/PortfolioAnalyticsPage'));
const PortfolioHealthPage = lazy(() => import('./pages/PortfolioHealthPage'));
const LivePortfolioPage = lazy(() => import('./pages/LivePortfolioPage'));
// PriceAlertsPage lazy import removed - now embedded in AlertsPage tabs (Chunk 175)
// const PriceAlertsPage = lazy(() =>
//   import('./pages/PriceAlertsPage').then((m) => ({ default: m.PriceAlertsPage }))
// );
const DisplaySettingsPage = lazy(() => import('./pages/DisplaySettingsPage'));
const PreferencesPage = lazy(() => import('./pages/settings/PreferencesPage'));
const TaxOptimisationPage = lazy(() => import('./pages/TaxOptimisationPage'));
const TaxSettingsPage = lazy(() => import('./pages/settings/TaxSettingsPage'));
const NFTPortfolioPage = lazy(() => import('./pages/NFTPortfolioPage'));
const ExchangeSyncPage = lazy(() => import('./pages/ExchangeSyncPage'));
const OnChainAnalyticsPage = lazy(() => import('./pages/OnChainAnalyticsPage'));
const PublicPortfolioPage = lazy(() => import('./pages/PublicPortfolioPage'));
const AIInsightsPage = lazy(() => import('./pages/AIInsightsPage'));
const AIPortfolioOptimizerPage = lazy(() => import('./pages/AIPortfolioOptimizerPage'));
const InsightsFeedPage = lazy(() => import('./pages/InsightsFeedPage'));
const BridgeMonitorPage = lazy(() => import('./pages/BridgeMonitorPage'));
const GovernancePage = lazy(() => import('./pages/GovernancePage'));
const GasAnalyticsPage = lazy(() => import('./pages/GasAnalyticsPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const PortfolioSimulationPage = lazy(() => import('./pages/PortfolioSimulationPage'));
const GrowthPage = lazy(() => import('./pages/GrowthPage'));
const CommunityLeaderboardPage = lazy(() => import('./pages/CommunityLeaderboardPage'));
const TaxExportPage = lazy(() => import('./pages/TaxExportPage'));
const OrgDashboardPage = lazy(() => import('./pages/OrgDashboardPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AttributionAnalysisPage = lazy(() => import('./pages/AttributionAnalysisPage'));
const StakingAnalyticsPage = lazy(() => import('./pages/StakingAnalyticsPage'));
const StakingOptimizerPage = lazy(() => import('./pages/StakingOptimizerPage'));
const ProtocolRiskPage = lazy(() => import('./pages/ProtocolRiskPage'));
const CompoundAlertRulesPage = lazy(() => import('./pages/CompoundAlertRulesPage'));
const ComplianceReportingPage = lazy(() => import('./pages/ComplianceReportingPage'));
const ComplianceSchedulingPage = lazy(() => import('./pages/ComplianceSchedulingPage'));
const AutomationRulesPage = lazy(() =>
  import('./pages/AutomationRulesPage').then((m) => ({ default: m.AutomationRulesPage }))
);
const DCASchedulesPage = lazy(() => import('./pages/DCASchedulesPage'));
const PortfolioComparisonPage = lazy(() => import('./pages/PortfolioComparisonPage'));
const WatchlistPage = lazy(() => import('./pages/WatchlistPage'));
const SsoCallbackPage = lazy(() => import('./pages/SsoCallbackPage'));
const SSOConfigPage = lazy(() => import('./pages/admin/SSOConfigPage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const WorkspaceListPage = lazy(() => import('./pages/WorkspaceListPage'));
const WorkspaceAcceptInvitePage = lazy(() => import('./pages/WorkspaceAcceptInvitePage'));
const AdvisorOverviewPage = lazy(() => import('./pages/AdvisorOverviewPage'));
const RebalancingTargetsPage = lazy(() => import('./pages/RebalancingTargetsPage'));
const PortfolioGoalsPage = lazy(() => import('./pages/PortfolioGoalsPage'));
const BridgeTrackerPage = lazy(() => import('./pages/BridgeTrackerPage'));
const MarketOverviewPage = lazy(() => import('./pages/MarketOverviewPage'));
const MultisigWalletsPage = lazy(() => import('./pages/MultisigWalletsPage'));
const BacktestPage = lazy(() => import('./pages/BacktestPage'));
const YieldScannerPage = lazy(() => import('./pages/YieldScannerPage'));
const AISignalsPage = lazy(() => import('./pages/AISignalsPage'));
const WhaleTrackerPage = lazy(() => import('./pages/WhaleTrackerPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'));

function ProtectedShell({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <PushNotificationSetup />
      <PageShell>
        <ErrorBoundary>{children}</ErrorBoundary>
      </PageShell>
      <AdvisorChat />
    </ProtectedRoute>
  );
}

/** Activates page-view tracking for every navigation event. Must be inside <BrowserRouter>. */
function PageTracker() {
  usePageTracking();
  return null;
}

/**
 * Chunk 153 — Activates mobile push notification registration for authenticated users.
 * No-op on web; runs only inside Capacitor native shell (iOS/Android).
 */
function PushNotificationSetup() {
  usePushNotifications();
  return null;
}

/** Shows LandingPage to unauthenticated visitors; redirects authenticated users to /dashboard. */
/**
 * Smart root route component.
 * Shows the public LandingPage to unauthenticated visitors.
 * Redirects authenticated users straight to /dashboard.
 */
function RootRoute() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function App() {
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      void store.dispatch(getCurrentUser()).then((action) => {
        if (getCurrentUser.fulfilled.match(action)) {
          const user = action.payload;
          // Identify user in PostHog for session continuity and funnel analysis
          posthog.identify(user.id, {
            email: user.email,
            name: user.displayName || user.email,
            created_at: user.createdAt,
          });
          // Fire a reliable custom event — works regardless of which page the user visits
          trackSessionRestored(user.id);
        }
      });
    }
  }, []);

  return (
    <Provider store={store}>
      <CurrencyProvider>
        <BrowserRouter>
          <PageTracker />
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/pricing" element={<PricingPage />} />
            {/* SEO landing pages */}
            <Route path="/defi-risk-monitor" element={<DeFiRiskLandingPage />} />
            <Route path="/multi-chain-crypto-portfolio" element={<MultiChainPortfolioPage />} />
            <Route path="/defi-lp-tax-calculator" element={<DeFiLpTaxCalculatorPage />} />
            <Route path="/koinly-alternative" element={<KoinlyAlternativePage />} />
            <Route path="/cointracker-alternative" element={<CoinTrackerAlternativePage />} />
            <Route path="/zapper-alternative" element={<ZapperAlternativePage />} />
            <Route path="/debank-alternative" element={<DeBankAlternativePage />} />
            <Route path="/rotki-alternative" element={<RotkiAlternativePage />} />
            <Route path="/coinstats-alternative" element={<CoinStatsAlternativePage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/password-reset" element={<ForgotPasswordPage />} />
            <Route path="/password-reset/confirm" element={<ResetPasswordPage />} />
            <Route path="/share/:shareToken" element={<PublicPortfolioPage />} />
            <Route path="/portfolio/share/:token" element={<PublicPortfolioPage />} />
            {/* SSO callback — receives accessToken/refreshToken after IdP redirect (Chunk 125) */}
            <Route path="/sso-callback" element={<SsoCallbackPage />} />
            {/* Market Overview — public page, no auth required (Chunk 155) */}
            <Route path="/market" element={<MarketOverviewPage />} />
            {/* Community Leaderboard — public discovery page, no auth required (Chunk 159) */}
            <Route path="/community" element={<CommunityLeaderboardPage />} />

            {/* Protected routes wrapped in PageShell */}
            <Route path="/dashboard" element={<ProtectedShell><DashboardPage /></ProtectedShell>} />
            <Route path="/alerts" element={<ProtectedShell><AlertsPage /></ProtectedShell>} />
            <Route path="/notifications" element={<ProtectedShell><NotificationSettingsPage /></ProtectedShell>} />
            <Route path="/risk-intelligence" element={<ProtectedShell><RiskIntelligenceCenterPage /></ProtectedShell>} />
            {/* Redirect old standalone risk pages to consolidated tabbed page */}
            <Route path="/risk-analytics" element={<Navigate to="/risk-intelligence?tab=analytics" replace />} />
            <Route path="/stress-test" element={<Navigate to="/risk-intelligence?tab=stress-test" replace />} />
            <Route path="/attribution" element={<ProtectedShell><AttributionAnalysisPage /></ProtectedShell>} />
            <Route path="/defi-health" element={<ProtectedShell><DeFiHealthPage /></ProtectedShell>} />
            <Route path="/protocol-risk" element={<ProtectedShell><ProtocolRiskPage /></ProtectedShell>} />
            <Route path="/staking" element={<ProtectedShell><StakingAnalyticsPage /></ProtectedShell>} />
            <Route path="/staking-optimizer" element={<ProtectedShell><StakingOptimizerPage /></ProtectedShell>} />
            <Route path="/defi/impermanent-loss" element={<ProtectedShell><ImpermanentLossPage /></ProtectedShell>} />
            <Route path="/alert-rules" element={<ProtectedShell><AlertRulesPage /></ProtectedShell>} />
            <Route path="/alert-rules/compound" element={<ProtectedShell><CompoundAlertRulesPage /></ProtectedShell>} />
            <Route path="/rebalancing-planner" element={<ProtectedShell><RebalancingPlannerPage /></ProtectedShell>} />
            <Route path="/rebalancing" element={<ProtectedShell><RebalancingAdvisorPage /></ProtectedShell>} />
            <Route path="/risk" element={<ProtectedShell><RiskPage /></ProtectedShell>} />
            <Route path="/wallets" element={<ProtectedShell><WalletsPage /></ProtectedShell>} />
            <Route path="/transactions" element={<ProtectedShell><TransactionsPage /></ProtectedShell>} />
            <Route path="/import" element={<ProtectedShell><PortfolioImportPage /></ProtectedShell>} />
            <Route path="/wallet-hub" element={<ProtectedShell><WalletHubPage /></ProtectedShell>} />
            <Route path="/multisig" element={<ProtectedShell><MultisigWalletsPage /></ProtectedShell>} />
            <Route path="/audit-logs" element={<ProtectedShell><AuditLogsPage /></ProtectedShell>} />
            <Route path="/admin/audit-dashboard" element={<ProtectedShell><AuditDashboardPage /></ProtectedShell>} />
            <Route path="/privacy-settings" element={<ProtectedShell><PrivacySettingsPage /></ProtectedShell>} />
            <Route path="/data-export" element={<ProtectedShell><DataExportPage /></ProtectedShell>} />
            <Route path="/account-deletion" element={<ProtectedShell><AccountDeletionPage /></ProtectedShell>} />
            <Route path="/display-settings" element={<ProtectedShell><DisplaySettingsPage /></ProtectedShell>} />
            <Route path="/security-defenses" element={<ProtectedShell><SecurityDefensesPage /></ProtectedShell>} />
            <Route path="/debug" element={<ProtectedShell><DebugPage /></ProtectedShell>} />
            <Route path="/exports" element={<ProtectedShell><ExportStatusPage /></ProtectedShell>} />
            <Route path="/alerts/deliveries" element={<ProtectedShell><AlertDeliveryHistoryPage /></ProtectedShell>} />

            {/* Billing routes (Chunk 48 + 51) */}
            <Route path="/billing/subscription" element={<ProtectedShell><SubscriptionPage /></ProtectedShell>} />
            <Route path="/billing/plans" element={<ProtectedShell><PlansPage /></ProtectedShell>} />
            <Route path="/billing/upgrade" element={<ProtectedShell><UpgradePage /></ProtectedShell>} />
            <Route path="/billing/invoices" element={<ProtectedShell><InvoicesPage /></ProtectedShell>} />
            <Route path="/billing/success" element={<ProtectedShell><BillingSuccessPage /></ProtectedShell>} />

            {/* Lifecycle routes (Chunk 49) */}
            <Route path="/onboarding" element={<ProtectedShell><OnboardingPage /></ProtectedShell>} />
            <Route path="/settings/campaign-history" element={<ProtectedShell><CampaignHistoryPage /></ProtectedShell>} />
            <Route path="/settings/security" element={<ProtectedShell><SecuritySettingsPage /></ProtectedShell>} />
            <Route path="/settings/tax" element={<ProtectedShell><TaxSettingsPage /></ProtectedShell>} />
            <Route path="/settings/notifications" element={<ProtectedShell><NotificationPreferencesPage /></ProtectedShell>} />
            <Route path="/settings/language" element={<ProtectedShell><PreferencesPage /></ProtectedShell>} />

            {/* Tax Optimisation (Chunk 68) */}
            <Route path="/tax-optimisation" element={<ProtectedShell><TaxOptimisationPage /></ProtectedShell>} />

            {/* NFT Portfolio (Chunk 70) */}
            <Route path="/nft-portfolio" element={<ProtectedShell><NFTPortfolioPage /></ProtectedShell>} />

            {/* Multi-Exchange Direct Sync (Chunk 72) */}
            <Route path="/exchange-sync" element={<ProtectedShell><ExchangeSyncPage /></ProtectedShell>} />
            <Route path="/onchain-analytics" element={<ProtectedShell><OnChainAnalyticsPage /></ProtectedShell>} />
            <Route path="/ai-insights" element={<ProtectedShell><AIInsightsPage /></ProtectedShell>} />
            <Route path="/ai-optimizer" element={<ProtectedShell><AIPortfolioOptimizerPage /></ProtectedShell>} />
            <Route path="/insights" element={<ProtectedShell><InsightsFeedPage /></ProtectedShell>} />
            <Route path="/bridge-monitor" element={<ProtectedShell><BridgeMonitorPage /></ProtectedShell>} />
            <Route path="/governance" element={<ProtectedShell><GovernancePage /></ProtectedShell>} />
            <Route path="/gas-analytics" element={<ProtectedShell><GasAnalyticsPage /></ProtectedShell>} />
            <Route path="/simulation" element={<ProtectedShell><PortfolioSimulationPage /></ProtectedShell>} />
            <Route path="/growth" element={<ProtectedShell><GrowthPage /></ProtectedShell>} />
            <Route path="/tax-harvest" element={<Navigate to="/tax-optimisation?tab=harvesting" replace />} />
            {/* Tax Year-End Simulator (Chunk 110) */}
            <Route path="/tax/simulator" element={<Navigate to="/tax-optimisation?tab=simulator" replace />} />
            {/* Tax Filing Export Integration (Chunk 165) */}
            <Route path="/tax-export" element={<ProtectedShell><TaxExportPage /></ProtectedShell>} />
            {/* DeFi Automation Rules Engine (Chunk 111) */}
            <Route path="/automation" element={<ProtectedShell><AutomationRulesPage /></ProtectedShell>} />

            {/* DCA Schedules — Recurring Purchase Automation (Chunk 135) */}
            <Route path="/dca" element={<ProtectedShell><DCASchedulesPage /></ProtectedShell>} />

            {/* Portfolio Comparison & Custom Benchmark Builder (Chunk 113) */}
            <Route path="/comparison" element={<ProtectedShell><PortfolioComparisonPage /></ProtectedShell>} />

            {/* Advanced Watchlist & Research Hub (Chunk 130) */}
            <Route path="/watchlist" element={<ProtectedShell><WatchlistPage /></ProtectedShell>} />

            {/* Enterprise & White-Label (Chunk 86) */}
            <Route path="/org-dashboard" element={<ProtectedShell><OrgDashboardPage /></ProtectedShell>} />
            <Route path="/org/accept-invite" element={<AcceptInvitePage />} />

            {/* Compliance Reporting (Chunk 106) */}
            <Route path="/compliance" element={<ProtectedShell><ComplianceReportingPage /></ProtectedShell>} />

            {/* Compliance Scheduling Suite (Chunk 185) */}
            <Route path="/compliance/reports" element={<ProtectedShell><ComplianceSchedulingPage /></ProtectedShell>} />

            {/* Admin Dashboard (Chunk 90) */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <PageShell>
                    <ErrorBoundary>
                      <AdminDashboardPage />
                    </ErrorBoundary>
                  </PageShell>
                </AdminRoute>
              }
            />

            {/* Enterprise SSO Configuration (Chunk 125) */}
            <Route path="/admin/sso" element={<ProtectedShell><SSOConfigPage /></ProtectedShell>} />

            {/* Multi-User Portfolio Collaboration Workspace (Chunk 127) */}
            <Route path="/workspace/accept" element={<WorkspaceAcceptInvitePage />} />
            <Route path="/workspace/:id" element={<ProtectedShell><WorkspacePage /></ProtectedShell>} />
            <Route path="/workspaces" element={<ProtectedShell><WorkspaceListPage /></ProtectedShell>} />

            {/* Advisor Overview — Multi-Client Workspace Aggregation (Chunk 140) */}
            <Route path="/advisor-overview" element={<ProtectedShell><AdvisorOverviewPage /></ProtectedShell>} />

            {/* Portfolio Rebalancing Planner — Target Allocation Editor (Chunk 147) */}
            <Route path="/rebalancing-targets" element={<ProtectedShell><RebalancingTargetsPage /></ProtectedShell>} />
            <Route path="/goals" element={<ProtectedShell><PortfolioGoalsPage /></ProtectedShell>} />

            {/* Multi-Chain Bridge Transaction Tracking (Chunk 148) */}
            <Route path="/bridge-tracker" element={<ProtectedShell><BridgeTrackerPage /></ProtectedShell>} />

            {/* Partner API / Developer Portal (Chunk 50) */}
            <Route path="/developer" element={<ProtectedShell><DeveloperPortalPage /></ProtectedShell>} />
            <Route path="/developer/docs" element={<ProtectedShell><DeveloperDocsPage /></ProtectedShell>} />

            {/* Portfolio Analytics (Chunk 59) */}
            <Route path="/portfolio-analytics" element={<ProtectedShell><PortfolioAnalyticsPage /></ProtectedShell>} />
            <Route path="/portfolio-health" element={<ProtectedShell><PortfolioHealthPage /></ProtectedShell>} />
            {/* Real-Time Portfolio Streaming — Live P&L View (Chunk 183) */}
            <Route path="/live" element={<ProtectedShell><LivePortfolioPage /></ProtectedShell>} />
            {/* Portfolio Backtesting Engine (Chunk 184) */}
            <Route path="/backtest" element={<ProtectedShell><BacktestPage /></ProtectedShell>} />
            {/* Yield Aggregator & DeFi Opportunity Scanner (Chunk 187) */}
            <Route path="/yield-scanner" element={<ProtectedShell><YieldScannerPage /></ProtectedShell>} />
            <Route path="/signals" element={<ProtectedShell><AISignalsPage /></ProtectedShell>} />
            {/* Whale Tracker (Chunk 194) */}
            <Route path="/whale-tracker" element={<ProtectedShell><WhaleTrackerPage /></ProtectedShell>} />
            <Route path="/price-alerts" element={<Navigate to="/alerts?tab=price-alerts" replace />} />

            {/* Redirect root to dashboard */}
            {/* Root is now handled by RootRoute above */}

            {/* Legal / Compliance public pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfUsePage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </CurrencyProvider>
    </Provider>
  );
}

export default App;
